/**
 * Message Bubble Component - בועת הודעה
 * Displays individual chat messages
 */

import { User, AlertCircle, CheckCircle } from 'lucide-react'
import type { ChatMessage } from '../../types'

interface MessageBubbleProps {
  message: ChatMessage
  userName?: string
}

export default function MessageBubble({ message, userName }: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  // System messages (warnings, info)
  if (isSystem) {
    return (
      <div className="mb-4">
        <div className="alert alert-info text-sm">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{message.content}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex items-start gap-3 mb-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {isUser ? (
        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-primary-600" aria-hidden="true">
          <User size={20} className="text-white" aria-hidden="true" />
        </div>
      ) : (
        <img
          src="/iftach-avatar.jpg"
          alt=""
          aria-hidden="true"
          className="flex-shrink-0 w-10 h-10 rounded-full object-cover border-2 border-primary-200"
        />
      )}

      {/* Message Content */}
      <div className={`flex-1 max-w-[80%] ${isUser ? 'flex flex-col items-end' : ''}`}>
        {/* Name and timestamp */}
        <div className={`text-xs text-gray-500 mb-1 ${isUser ? 'text-left' : ''}`}>
          <span className="font-medium">{isUser ? userName || 'אתה' : 'יפתח ונגר'}</span>
          <span className="mx-1">•</span>
          <span>
            {new Date(message.timestamp).toLocaleTimeString('he-IL', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>

        {/* Message bubble */}
        <div
          className={`rounded-lg p-4 ${
            isUser
              ? 'bg-primary-600 text-white rounded-tl-none'
              : 'bg-gray-100 text-gray-900 rounded-tr-none'
          }`}
        >
          <div className="whitespace-pre-wrap break-words text-base leading-relaxed">{message.content}</div>

          {/* Recommendations indicator */}
          {!isUser && message.recommendationsReturned && message.recommendationsReturned.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-300">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle size={16} className="text-green-600" aria-hidden="true" />
                <span>{message.recommendationsReturned.length} המלצות הושמו</span>
              </div>
            </div>
          )}

        </div>

        {/* Token count - hidden by default, uncomment for debugging */}
        {/* {!isUser && message.tokenCount && (
          <div className="text-xs text-gray-400 mt-1">
            {message.tokenCount} tokens
          </div>
        )} */}
      </div>
    </div>
  )
}
